import { DEFAULT_GROUP_TOKEN, isGroupToken } from './itemGroups';

/**
 * The grouping period for the collection items page, and which of its groups
 * are collapsed.
 *
 * The period follows the grid/table mode's pattern exactly (utils/itemsView):
 * a per-collection localStorage preference SEEDS the page, and the active
 * period also rides in the URL (?group=) once the user touches the control,
 * so a link carries what its sender was looking at. `day` is the floor, and
 * is deleted from the URL rather than written.
 *
 * The collapse state is deliberately thinner than the period. Only two bits
 * survive a reload — whether NO DATE is collapsed, and whether "Collapse all"
 * was the last bulk action — because a specific date key ("2026-09-13") is
 * meaningless next week. Individual groups are remembered for the session, in
 * React state.
 *
 * Every storage access is guarded: a private window, blocked site data, or a
 * full quota must never take the list down.
 */

export const itemsGroupKey = collectionId => `keepr.itemsGroup.${collectionId || ''}`;

export const itemsGroupsCollapseKey = collectionId => `keepr.itemsGroups.${collectionId || ''}`;

export const readStoredItemsGroup = (collectionId) => {
	try {
		const stored = window.localStorage.getItem(itemsGroupKey(collectionId));
		return isGroupToken(stored) ? stored : DEFAULT_GROUP_TOKEN;
	} catch {
		return DEFAULT_GROUP_TOKEN;
	}
};

export const writeStoredItemsGroup = (collectionId, token) => {
	try {
		window.localStorage.setItem(itemsGroupKey(collectionId), token);
	} catch { /* per-viewer convenience only */ }
};

/**
 * The active grouping period as another surface would see it: the URL param
 * wins (it is what is on screen), the stored per-collection preference fills
 * in when the URL says nothing, and `day` is the floor.
 */
export const resolveItemsGroup = (searchParams, collectionId) => {
	const fromUrl = searchParams?.get('group');
	if (isGroupToken(fromUrl)) return fromUrl;
	return readStoredItemsGroup(collectionId);
};

/** `{ undated, all }` — the only collapse facts worth keeping past a reload. */
export const DEFAULT_GROUP_COLLAPSE = { undated: false, all: false };

export const readStoredGroupCollapse = (collectionId) => {
	try {
		const raw = window.localStorage.getItem(itemsGroupsCollapseKey(collectionId));
		if (!raw) return DEFAULT_GROUP_COLLAPSE;
		const parsed = JSON.parse(raw);
		return {
			undated: parsed?.undated === true,
			all: parsed?.all === true,
		};
	} catch {
		return DEFAULT_GROUP_COLLAPSE;
	}
};

export const writeStoredGroupCollapse = (collectionId, state) => {
	try {
		window.localStorage.setItem(
			itemsGroupsCollapseKey(collectionId),
			JSON.stringify({ undated: state?.undated === true, all: state?.all === true })
		);
	} catch { /* per-viewer convenience only */ }
};
